/**
 * 접수 불가 화면 — 접수 중지(enabled=false) · 비활성 QR(QR_INACTIVE) · 매장 조회 실패.
 *
 * 디자인 원본에는 없는 화면이다(데모는 항상 접수 중). 설계 §4/§8-1 이 요구하는
 * "접수 불가 안내"를 FormScreen 의 시각 언어(배지 + 제목 + 설명)로 구성했다.
 *
 * @see #781 / admin#2111 설계 §4 (QR_INACTIVE) / §8-1 (접수 중지)
 */
import { Ban } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { Locale } from '@/config/locales';
import type {
  WaitlistPauseReason,
  WaitlistQueueType,
  WaitlistStatusData,
} from '@/lib/api/waitlist';
import { getWaitlistPauseCopy, getWaitlistPauseReasonText } from './pauseCopy';
import { WaitlistAppBar } from './WaitlistParts';
import type { WaitlistStrings } from './strings';

export type WaitlistUnavailableReason = 'closed' | 'paused' | 'qr_inactive' | 'error';

interface WaitlistUnavailableScreenProps {
  t: WaitlistStrings;
  storeName: string;
  storeZone: string;
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  reason: WaitlistUnavailableReason;
  /** 일시적 실패(`error`)일 때만 제공 — 재시도 버튼 노출. */
  onRetry?: () => void;
  storeMeta?: WaitlistStatusData['store_meta'];
  /** reason=paused 전용. 고객에게 공개 가능한 enum 사유만 받는다. */
  pauseReason?: WaitlistPauseReason | null;
  pausedQueue?: WaitlistQueueType;
}

export function WaitlistUnavailableScreen({
  t,
  storeName,
  storeZone,
  locale,
  onLocaleChange,
  reason,
  onRetry,
  storeMeta,
  pauseReason,
  pausedQueue,
}: WaitlistUnavailableScreenProps) {
  const pauseCopy = getWaitlistPauseCopy(locale);
  const title =
    reason === 'qr_inactive'
      ? t.qrInactivo
      : reason === 'closed'
        ? t.filaCerrada
        : reason === 'paused'
          ? pauseCopy.title
          : t.errorGenerico;
  const description =
    reason === 'qr_inactive'
      ? t.qrInactivoDesc
      : reason === 'closed'
        ? t.cerradaDesc
        : reason === 'paused'
          ? pauseCopy.description
          : '';
  const badge =
    reason === 'qr_inactive'
      ? t.qrInactivo
      : reason === 'paused'
        ? `${pausedQueue === 'pickup' ? t.modePickup : t.modeTable} · ${pauseCopy.badge}`
        : t.filaCerrada;

  return (
    <>
      <WaitlistAppBar
        storeName={storeName}
        subtitle={storeZone}
        locale={locale}
        onLocaleChange={onLocaleChange}
        languageLabel={t.idioma}
        logoUrl={storeMeta?.logo_url}
        mapsUrl={storeMeta?.maps_url}
      />
      <div className="flex flex-1 flex-col items-center justify-center px-7 pb-10 text-center">
        <div className="mb-[22px] grid size-[84px] place-items-center rounded-full bg-background-secondary text-muted-foreground">
          <Ban className="size-10" />
        </div>
        <Badge variant="secondary" className="mb-3">
          {badge}
        </Badge>
        <h1 className="m-0 mb-2 text-2xl font-extrabold leading-tight">{title}</h1>
        {reason === 'paused' && (
          <p className="m-0 mb-2 max-w-[300px] text-[15px] font-semibold leading-relaxed text-foreground">
            {getWaitlistPauseReasonText(locale, pauseReason)}
          </p>
        )}
        {description && (
          <p className="m-0 max-w-[300px] text-[15px] leading-relaxed text-muted-foreground">
            {description}
          </p>
        )}
        {/* 재시도는 일시적 실패에만 — 접수 중지/비활성 QR 은 재시도해도 결과가 같다. */}
        {reason === 'error' && onRetry && (
          <Button size="lg" className="mt-8 w-full max-w-[300px]" onClick={onRetry}>
            {t.reintentar}
          </Button>
        )}
      </div>
    </>
  );
}
